import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  FormGroup,
  Label,
  Input,
  Button
} from 'reactstrap';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import moment from 'moment';
import PageDataLoader from '../../Utility/PageDataLoader';
import Paginator from '../../Utility/Paginator';
import Select from 'react-select';
import {
  buildWhatsAppMessageLogParams,
  exportWhatsAppMessageLogs,
  fetchWhatsAppMessageLogs
} from '../../../api/whatsapp';
import { getChildList } from '../../../api/child';
import {
  buildWhatsAppMessageLogsCsv,
  buildWhatsAppMessageLogsExportFilename,
  downloadCsv,
  enrichMessageLogForDisplay
} from '../../../helpers/exportWhatsAppMessageLogsCsv';
import ExportProgressOverlay from '../../Child/ExportProgressOverlay';
import WhatsAppMessageCell from './WhatsAppMessageCell';

const STATUS_OPTIONS = [
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'received', label: 'Received' }
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'outbound', label: 'Message Sent' },
  { value: 'inbound', label: 'Message Received' }
];

const STATUS_COLOR = {
  sent: 'warning',
  delivered: 'primary',
  read: 'success',
  failed: 'danger',
  received: 'success'
};

const MessageHistoryTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageLimit: 10,
    totalEntries: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    childId: '',
    phoneNumber: '',
    startTime: '',
    endTime: '',
    statuses: [],
    direction: '',
    purpose: ''
  });
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [childOptions, setChildOptions] = useState([]);
  const [childOptionsLoading, setChildOptionsLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [purposeInput, setPurposeInput] = useState('');
  const [sortingRule, setSortingRule] = useState('sentAt$desc');
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');

  const needsDateRangeForExport =
    pagination.totalEntries > 1000 && (!filters.startTime || !filters.endTime);

  const fetchLogs = useCallback(async (page, limit, activeFilters, sort_option) => {
    setLoading(true);
    try {
      const params = buildWhatsAppMessageLogParams(activeFilters, sort_option, page, limit);
      const response = await fetchWhatsAppMessageLogs(params);
      const now = moment();
      const data = (response?.data || []).map((log, idx) =>
        enrichMessageLogForDisplay(log, page, limit, idx, now)
      );
      setLogs(data);
      setPagination({
        currentPage: response?.metadata?.page || 1,
        pageLimit: response?.metadata?.limit || limit,
        totalPages: response?.metadata?.total_pages || 0,
        totalEntries: response?.metadata?.total_results || 0
      });
    } catch (err) {
      toast.error('Failed to fetch message history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1, pagination.pageLimit, filters, sortingRule);
  }, [filters, sortingRule]);

  const fetchChildOptions = async (searchParam) => {
    setChildOptionsLoading(true);
    try {
      const response = await getChildList(1, 50, searchParam && { filter_name: searchParam });
      setChildOptions(
        (response?.data || []).map((item) => ({ value: item._id, label: item.name }))
      );
    } catch {
      setChildOptions([]);
    } finally {
      setChildOptionsLoading(false);
    }
  };

  const searchChild = useCallback(
    debounce((inputValue) => {
      fetchChildOptions(inputValue);
    }, 300),
    []
  );

  const debouncedPhoneSearch = useCallback(
    debounce((value) => {
      setFilters((prev) => ({ ...prev, phoneNumber: value }));
    }, 400),
    []
  );

  const debouncedPurposeSearch = useCallback(
    debounce((value) => {
      setFilters((prev) => ({ ...prev, purpose: value }));
    }, 400),
    []
  );

  const handlePhoneChange = (e) => {
    setPhoneInput(e.target.value);
    debouncedPhoneSearch(e.target.value);
  };

  const handlePurposeChange = (e) => {
    setPurposeInput(e.target.value);
    debouncedPurposeSearch(e.target.value);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleChildChange = (option) => {
    setSelectedChild(option);
    setFilters((prev) => ({ ...prev, childId: option?.value || '' }));
  };

  const handleClearFilters = () => {
    setPhoneInput('');
    setPurposeInput('');
    setSelectedChild(null);
    setSelectedStatuses([]);
    setFilters({
      childId: '',
      phoneNumber: '',
      startTime: '',
      endTime: '',
      statuses: [],
      direction: '',
      purpose: ''
    });
    setSortingRule('sentAt$desc');
  };

  const handlePageChange = (page) => {
    fetchLogs(page, pagination.pageLimit, filters, sortingRule);
  };

  const handleExportCsv = async () => {
    setExportInProgress(true);
    setExportProgress(5);
    setExportMessage('Fetching messages...');

    try {
      const rows = await exportWhatsAppMessageLogs({
        filters,
        sort_option: sortingRule,
        onProgress: (pct) => {
          setExportProgress(pct);
          setExportMessage('Fetching messages...');
        }
      });

      setExportProgress(85);
      setExportMessage('Building CSV...');

      const csv = buildWhatsAppMessageLogsCsv(rows);
      const dateStr = moment().format('YYYY-MM-DD');
      downloadCsv(csv, buildWhatsAppMessageLogsExportFilename(filters.purpose, dateStr));

      setExportProgress(100);
      setExportMessage('Download ready');
      toast.success(`Exported ${rows.length} message(s)`);
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Export failed';
      toast.error(message);
    } finally {
      setTimeout(() => {
        setExportInProgress(false);
        setExportProgress(0);
        setExportMessage('');
      }, 400);
    }
  };

  return (
    <Card>
      <ExportProgressOverlay
        visible={exportInProgress}
        progress={exportProgress}
        message={exportMessage}
      />
      <CardBody>
        {/* Filters */}
        <Row className="mb-2">
          <Col md="3">
            <FormGroup>
              <Label>Child</Label>
              <Select
                isClearable
                options={childOptions}
                value={selectedChild}
                onChange={handleChildChange}
                onInputChange={(value, { action }) => {
                  if (action === 'input-change') searchChild(value);
                }}
                onMenuOpen={() => {
                  if (!childOptions.length) fetchChildOptions('');
                }}
                isLoading={childOptionsLoading}
                placeholder="Type to search child..."
                isDisabled={loading}
              />
              {selectedChild && !filters.startTime && !filters.endTime && (
                <small className="text-muted">Add a date range for faster results</small>
              )}
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Mobile Number</Label>
              <Input
                type="text"
                value={phoneInput}
                onChange={handlePhoneChange}
                placeholder="e.g. 9819028106"
                disabled={loading}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Purpose</Label>
              <Input
                type="text"
                value={purposeInput}
                onChange={handlePurposeChange}
                placeholder="e.g. Fee reminder"
                disabled={loading}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>From Date</Label>
              <Input
                type="date"
                value={filters.startTime}
                onChange={(e) => handleFilterChange('startTime', e.target.value)}
                disabled={loading}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>To Date</Label>
              <Input
                type="date"
                value={filters.endTime}
                onChange={(e) => handleFilterChange('endTime', e.target.value)}
                disabled={loading}
              />
              {needsDateRangeForExport && (
                <small className="text-warning">
                  Set From and To dates to export more than 1,000 messages
                </small>
              )}
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Status</Label>
              <Select
                isMulti
                isClearable
                options={STATUS_OPTIONS}
                value={selectedStatuses}
                onChange={(options) => {
                  const selected = options || [];
                  setSelectedStatuses(selected);
                  setFilters((prev) => ({
                    ...prev,
                    statuses: selected.map((o) => o.value)
                  }));
                }}
                placeholder="All statuses"
                isDisabled={loading}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Mode</Label>
              <Input
                type="select"
                value={filters.direction}
                onChange={(e) => handleFilterChange('direction', e.target.value)}
                disabled={loading}
              >
                {DIRECTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md="12" className="d-flex align-items-center">
            <Button color="secondary" outline size="sm" onClick={handleClearFilters} disabled={loading}>
              Clear Filters
            </Button>
            <Button
              color="info"
              size="sm"
              className="ml-2"
              onClick={handleExportCsv}
              disabled={
                loading ||
                exportInProgress ||
                pagination.totalEntries === 0
              }
            >
              <i className="ri-download-2-line align-middle mr-1" />
              Export CSV
            </Button>
          </Col>
        </Row>

        {/* Show entries + sort */}
        <Row className="mb-2">
          <Col md="2">
            <FormGroup>
              <Label>Show Entries</Label>
              <Input
                type="select"
                value={pagination.pageLimit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setPagination((prev) => ({ ...prev, pageLimit: newLimit }));
                  fetchLogs(1, newLimit, filters, sortingRule);
                }}
                disabled={loading}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label>Sort</Label>
              <Input
                type="select"
                value={sortingRule}
                onChange={(e) => setSortingRule(e.target.value)}
                disabled={loading}
              >
                <option value="sentAt$desc">Newest First</option>
                <option value="sentAt$asc">Oldest First</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {/* Table */}
        {loading ? (
          <div className="text-center py-4">
            <PageDataLoader height={40} width={40} />
          </div>
        ) : (
          <div className="table-rep-plugin">
            <div className="table-responsive mb-0">
              <Table striped bordered responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Message Sent</th>
                    <th>Status Updated</th>
                    <th>Child Name</th>
                    <th>Purpose</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Mode</th>
                    <th>Failure Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-3">
                        No messages found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log._id}>
                        <td>{log.srNo}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{log.sentAtFormatted}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{log.statusUpdatedAtFormatted}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {log.childId ? (
                            <Link
                              to={`/children/view/${log.childId?._id || log.childId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {log.childName || 'View child'}
                            </Link>
                          ) : (
                            log.childName || log.displayPhone || '—'
                          )}
                        </td>
                        <td style={{ maxWidth: '160px' }}>{log.purpose || '—'}</td>
                        <td style={{ maxWidth: '280px' }}>
                          <WhatsAppMessageCell
                            message={log.message}
                            rowId={log._id}
                            context={{
                              phone: log.displayPhone,
                              purpose: log.purpose,
                              sentAt: log.sentAtFormatted
                            }}
                          />
                        </td>
                        <td>
                          {log.isStale ? (
                            <span title="Sent over 24 hours ago with no delivery update from Meta">
                              <Badge color="secondary">sent</Badge>{' '}
                              <small className="text-muted">no update</small>
                            </span>
                          ) : (
                            <Badge color={STATUS_COLOR[log.status] || 'secondary'}>
                              {log.status}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge color={log.direction === 'outbound' ? 'primary' : 'success'}>
                            {log.direction === 'outbound' ? 'Message Sent' : 'Message Received'}
                          </Badge>
                        </td>
                        <td>
                          {log.failureReason ? (
                            <span className="text-danger" style={{ fontSize: '0.85em' }}>
                              {log.failureCode ? <strong>[{log.failureCode}]</strong> : null}{' '}
                              {log.failureReason}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Footer */}
        <Row className="mt-2">
          <Col md="5">
            <p>
              <strong>
                Showing {pagination.totalEntries === 0 ? 0 : (pagination.currentPage - 1) * pagination.pageLimit + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.pageLimit, pagination.totalEntries)} of{' '}
                {pagination.totalEntries} entries
              </strong>
            </p>
          </Col>
          {!loading && logs.length > 0 && (
            <Col md="7">
              <Paginator
                totalPages={pagination.totalPages}
                currentPage={pagination.currentPage}
                changePage={handlePageChange}
              />
            </Col>
          )}
        </Row>
      </CardBody>
    </Card>
  );
};

export default MessageHistoryTab;
